import { AzureAvatarService } from "@service/avatar/azure-avatar.service";
import { useDispatch } from "react-redux";
import { useSendAudioMessageMutation } from "@store/companionApi";
import { setProcessing, setTalking, setText } from "@store/avatarSlice";
import splitTextToSentences from "@utils/splitTextToSentences";
import { MutableRefObject, useCallback } from "react";
import { EventType } from '@models/audio-message';

const MAX_SENTENCE_LENGTH = 500;

const useBatching = ({
  sessionId,
  avatar,
}: {
  sessionId: string;
  avatar: MutableRefObject<AzureAvatarService | null>;
}) => {
  const dispatch = useDispatch();
  const [sendAudioMessage] = useSendAudioMessageMutation();

  const speakSentence = useCallback(
    async (sentence: string) => {
      if (!avatar.current?.running) {
        return;
      }
      await avatar.current.speak(sentence);
    },
    [avatar],
  );

  const speakSentences = useCallback(
    async (sentences: string[]) => {
      dispatch(setTalking(true));
      try {
        for (const sentence of sentences) {
          await speakSentence(sentence);
        }
      } catch (e) {
        console.error(e);
      } finally {
        dispatch(setTalking(false));
      }
    },
    [dispatch, speakSentence],
  );

  const sendMessage = useCallback(
    async (audioBlob?: Blob, photoId?: string, eventType?: EventType): Promise<void> => {
      dispatch(setProcessing(true));
      try {
        const { data } = await sendAudioMessage({
          audio: audioBlob || null,
          sessionId,
          photoId: photoId || null,
          eventType: eventType || null,
        });

        if (!data) {
          return;
        }

        const { aiMessage } = data;

        if (!aiMessage) {
          return;
        }
        dispatch(setText(aiMessage));
        const sentences = splitTextToSentences(aiMessage, MAX_SENTENCE_LENGTH);

        await speakSentences(sentences);
      } catch (e) {
        console.error(e);
      } finally {
        dispatch(setProcessing(false));
      }
    },
    [dispatch, sendAudioMessage, sessionId, speakSentences],
  );

  return sendMessage;
};

export default useBatching;
